import { useState } from "react";
import type { DonationDto } from "../lib/api";
import { displayTitle, formatMoney } from "../lib/youtube";
import { useVideoTitle } from "../hooks/useVideoTitle";

const time = (iso: string) =>
  new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

/** Speech bubble — `fill` follows CSS, so it fills in once a note exists. */
function NoteIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
      <path
        d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Broken chain-link — detach a matched donation from its request. */
function UnlinkIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
      <path
        d="M9 15l6-6M11 6l1-1a3 3 0 0 1 4 4l-1 1M13 18l-1 1a3 3 0 0 1-4-4l1-1M4 4l3 3M17 17l3 3"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Trash can — permanently delete a still-credited donation. */
function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
      <path
        d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const NOTES_MAX = 500;

/** A candidate request this donation's credit could be manually linked to. */
export interface LinkTarget {
  id: string;
  youtubeUrl?: string;
  /** Raw chat text — only set when the request never resolved to a video URL. */
  chatTitle?: string;
  matchedCents: number;
}

/** One `<option>` — its own component so it can resolve the real video title via oEmbed. */
function LinkOption({ target }: { target: LinkTarget }) {
  const resolvedTitle = useVideoTitle(target.youtubeUrl ?? "");
  const title = resolvedTitle ?? target.chatTitle ?? displayTitle(target.youtubeUrl ?? "");
  return (
    <option value={target.id}>
      {title} — has {formatMoney(target.matchedCents)}
    </option>
  );
}

/**
 * One donation row. A donation is either applied to a request (`matched`) or
 * sitting in the donor's credit pool (`credit`) — the chip says which, mirroring
 * the queue's paid/free colours. Notes are a streamer-only annotation, edited
 * in place like a request's note (see `RequestCard`).
 */
export function DonationRow({
  donation,
  onEditNotes,
  onUnlink,
  onDelete,
  linkTargets,
  onLink,
  busy,
}: {
  donation: DonationDto;
  onEditNotes?: (notes: string) => void;
  onUnlink?: () => void;
  /** Permanently delete — only offered while the donation is still credited. */
  onDelete?: () => void;
  /** This donor's own free requests — offered when the donation is still credited. */
  linkTargets?: LinkTarget[];
  onLink?: (requestId: string) => void;
  busy?: boolean;
}) {
  const state = donation.matchedRequestId ? "matched" : donation.creditedAt ? "credit" : "";

  const [editingNote, setEditingNote] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");

  function openNoteEditor() {
    setNoteDraft(donation.notes ?? "");
    setEditingNote(true);
  }

  function commitNote() {
    const next = noteDraft.trim();
    if (next !== (donation.notes ?? "")) onEditNotes?.(next);
    setEditingNote(false);
  }

  return (
    <div className={`card donation ${state}`}>
      <div className="body">
        <div className="meta">
          <span className="badge amount">{formatMoney(donation.amountCents)}</span>
          <span className={`badge platform ${donation.platform}`}>
            {donation.platform}
          </span>
          <span>@{donation.donorUsername}</span>
          {state === "matched" && <span className="badge matched">matched</span>}
          {state === "credit" && <span className="badge credit">credit</span>}
          <span className="spacer" />
          <span className="donation-time">{time(donation.donatedAt)}</span>
        </div>
        {donation.message && <div className="donation-msg">{donation.message}</div>}

        {editingNote ? (
          <div className="note-edit">
            <textarea
              value={noteDraft}
              autoFocus
              rows={2}
              maxLength={NOTES_MAX}
              disabled={busy}
              placeholder="Add a note…"
              onChange={(e) => setNoteDraft(e.target.value)}
              onKeyDown={(e) => {
                // Enter saves; Shift+Enter inserts a newline.
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  commitNote();
                }
                if (e.key === "Escape") setEditingNote(false);
              }}
              aria-label="Donation note"
            />
            <div className="note-edit-actions">
              <button className="btn" onClick={commitNote} disabled={busy}>
                Save
              </button>
              <button
                className="btn ghost"
                onClick={() => setEditingNote(false)}
                disabled={busy}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          donation.notes && (
            <div
              className={`note${onEditNotes ? " editable" : ""}`}
              onClick={onEditNotes ? openNoteEditor : undefined}
              title={onEditNotes ? "Click to edit note" : undefined}
            >
              {donation.notes}
            </div>
          )
        )}

        {state === "credit" && donation.linkable && onLink && !!linkTargets?.length && (
          <div className="donation-link-picker">
            <select
              value=""
              disabled={busy}
              aria-label="Link to a request"
              onChange={(e) => {
                if (e.target.value) onLink(e.target.value);
              }}
            >
              <option value="" disabled>
                Link to request…
              </option>
              {linkTargets.map((t) => (
                <LinkOption key={t.id} target={t} />
              ))}
            </select>
          </div>
        )}
      </div>

      {!editingNote &&
        (onEditNotes ||
          (onUnlink && state === "matched") ||
          (onDelete && state === "credit")) && (
        <div className="actions">
          {onEditNotes && (
            <button
              className={`btn icon note-btn${donation.notes ? " has-note" : ""}`}
              onClick={openNoteEditor}
              disabled={busy}
              aria-label={donation.notes ? "Edit note" : "Add note"}
              title={donation.notes ? "Edit note" : "Add a note"}
            >
              <NoteIcon />
            </button>
          )}
          {onUnlink && state === "matched" && (
            <button
              className="btn icon danger"
              onClick={onUnlink}
              disabled={busy}
              aria-label="Unlink from request"
              title="Detach this donation from its request — the money returns to the donor's credit"
            >
              <UnlinkIcon />
            </button>
          )}
          {onDelete && state === "credit" && (
            <button
              className="btn icon danger"
              onClick={onDelete}
              disabled={busy}
              aria-label="Delete donation"
              title="Permanently delete this donation"
            >
              <TrashIcon />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
